import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, ListGroup, Badge, Form, InputGroup, Button } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'; // Import MapContainer, TileLayer, Marker, Popup
import { FaMinus, FaPlus } from 'react-icons/fa'; // Import icons

interface Message {
  id: string;
  senderId: string;
  recipientId: string;
  content: string;
  timestamp: number;
  gameAddress: string; // New field for game address
  gameDate: string; // New field for game date
  gameTime: string; // New field for game time
  senderLatitude: number; // New field for sender's latitude
  senderLongitude: number; // New field for sender's longitude
  searcherId: string; // New field to link messages to a specific searcher session
  senderName?: string; // Optional sender name
}

interface PlayerChatProps {
  registeredPlayerId: string | null;
  registeredPlayerLocation: { latitude: number; longitude: number } | null;
  onNewMessage?: (searcherId: string) => void; // Optional callback for new messages, now accepts searcherId
  registeredPlayerName: string | null; // New prop for registered player's name
  searcherId: string;
  onCloseChat: () => void;
  searcherName?: string; // New prop for the searcher's name
  searcherGameAddress?: string; // New prop for the searcher's game address
  searcherGameDate?: string; // New prop for the searcher's game date
  searcherGameTime?: string; // New prop for the searcher's game time
  isMaximized: boolean; // New prop to indicate if this chat is maximized
  onMaximizeToggle: (chatId: string | null) => void; // New prop to toggle maximization
  unreadCount: number; // New prop for unread message count
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const PlayerChat: React.FC<PlayerChatProps> = ({ registeredPlayerId, registeredPlayerLocation, onNewMessage, registeredPlayerName, searcherId, onCloseChat, searcherName, searcherGameAddress, searcherGameDate, searcherGameTime, isMaximized, onMaximizeToggle, unreadCount }) => {
  console.log('PlayerChat props:', { registeredPlayerId, registeredPlayerLocation, registeredPlayerName, searcherId, searcherName, searcherGameAddress, isMaximized, unreadCount });
  console.log('PlayerChat: unreadCount for this chat:', unreadCount); // Added console.log
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [gameLocation, setGameLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([-34.6037, -58.3816]); // Default to Buenos Aires
  const [replyContent, setReplyContent] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null); // Ref for the end of messages

  console.log('PlayerChat: Current messages state:', messages);

  const toggleMinimize = () => {
    if (isMaximized) {
      onMaximizeToggle(null); // Minimize this chat
    } else {
      onMaximizeToggle(searcherId); // Maximize this chat
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]); // Scroll to bottom when messages change

  const getMapLink = (address: string) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const handleReply = async () => {
    if (!registeredPlayerId || !replyContent || messages.length === 0 || !registeredPlayerName) return;

    const lastMessage = messages[messages.length - 1];
    const recipientId = searcherId; // Reply to the sender of the last message

    try {
      const response = await fetch(`${backendUrl}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          senderId: registeredPlayerId,
          recipientId,
          content: replyContent,
          gameAddress: lastMessage.gameAddress, // Use the game address from the last message
          gameDate: lastMessage.gameDate, // Use the game date from the last message
          gameTime: lastMessage.gameTime, // Use the game time from the last message
          senderLatitude: registeredPlayerLocation?.latitude || 0,
          senderLongitude: registeredPlayerLocation?.longitude || 0,
          searcherId: lastMessage.searcherId, // Include the searcherId from the last message
          senderName: registeredPlayerName, // Include sender name
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Message sent, backend response data:', data.data);
        setReplyContent(''); // Clear input
        setMessages(prevMessages => [...prevMessages, data.data]);
      } else {
        console.error('Error sending reply:', await response.json());
      }
    } catch (error) {
      console.error('Network error sending reply:', error);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let longPollTimeout: NodeJS.Timeout | undefined;

    const fetchAllMessages = async () => {
      if (!registeredPlayerId) return;
      try {
        const response = await fetch(`${backendUrl}/api/messages/all/${registeredPlayerId}/${searcherId}`);
        const data = await response.json();
        if (isMounted && response.ok && data.messages) {
          console.log('PlayerChat fetchAllMessages:', data.messages);
          setMessages(data.messages);
          // Find the first message from the searcher to set the initial game location
          const searcherInitialMessage = data.messages.find((msg: Message) => msg.senderId === searcherId);
          if (searcherInitialMessage && typeof searcherInitialMessage.senderLatitude === 'number' && typeof searcherInitialMessage.senderLongitude === 'number') {
            setGameLocation({ latitude: searcherInitialMessage.senderLatitude, longitude: searcherInitialMessage.senderLongitude });
            setMapCenter([searcherInitialMessage.senderLatitude, searcherInitialMessage.senderLongitude]);
          }
        }
      } catch (error) {
        console.error('Error fetching all messages:', error);
      }
    };

    const pollForMessages = async () => {
      if (!registeredPlayerId) return; // Only poll if a player is registered

      try {
        console.log(`PlayerChat: Initiating long poll for player ${registeredPlayerId} and searcher ${searcherId} at ${backendUrl}/api/messages/${registeredPlayerId}/${searcherId}`);
        const response = await fetch(`${backendUrl}/api/messages/${registeredPlayerId}/${searcherId}`);
        const data = await response.json();
        console.log('PlayerChat: Raw data from long polling:', data);

        if (isMounted && response.ok && data.newMessage) {
          console.log('New message received from long polling:', data.newMessage);
          setMessages(prevMessages => {
            // Check if the message already exists to prevent duplicates
            if (prevMessages.some(msg => msg.id === data.newMessage.id)) {
              return prevMessages; // Message already exists, don't add again
            }
            if (
              data.newMessage.senderId === searcherId &&
              typeof data.newMessage.senderLatitude === 'number' &&
              typeof data.newMessage.senderLongitude === 'number'
            ) {
              setGameLocation({
                latitude: data.newMessage.senderLatitude,
                longitude: data.newMessage.senderLongitude,
              });
              setMapCenter([data.newMessage.senderLatitude, data.newMessage.senderLongitude]);
            }
            return [...prevMessages, data.newMessage];
          });
          if (onNewMessage) {
            onNewMessage(data.newMessage.searcherId);
          }
        }
      } catch (error) {
        console.error('Long polling for messages error:', error);
      } finally {
        if (isMounted) {
          // Re-initiate polling after a short delay or immediately
          longPollTimeout = setTimeout(pollForMessages, 1000); // Short delay to prevent hammering the server
        }
      }
    };

    fetchAllMessages(); // Fetch all messages initially
    pollForMessages(); // Start polling for new messages

    return () => {
      isMounted = false;
      if (longPollTimeout) {
        clearTimeout(longPollTimeout);
      }
    };
  }, [registeredPlayerId, searcherId]); // Re-run effect if registeredPlayerId or searcherId changes

  return (
    <>
      {!isMaximized ? (
        <div
          className="chat-bubble d-flex align-items-center justify-content-around"
          onClick={toggleMinimize}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            borderRadius: '30px',
            padding: '10px 15px',
            minWidth: '120px',
            height: '60px',
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            zIndex: 1000,
          }}
        >
          <span>{searcherName || searcherId} {searcherGameDate && searcherGameTime && `(${searcherGameDate} ${searcherGameTime})`} {searcherGameAddress && `(${searcherGameAddress})`}</span>
          {unreadCount > 0 && (
            <Badge bg="danger" pill className="ms-2">
              {unreadCount}
            </Badge>
          )}
          <FaPlus size={20} className="ms-2" />
        </div>
      ) : (
        <Card className="mt-4" style={{ maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
          <Card.Header className="d-flex justify-content-between align-items-center">
            {t('yourMessages')}
            <div>
              <Button variant="link" onClick={toggleMinimize} className="p-0 text-dark me-2">
                <FaMinus />
              </Button>
              <Button variant="link" onClick={onCloseChat} className="p-0 text-white">
                X
              </Button>
            </div>
          </Card.Header>
          <Card.Body style={{ flex: 1, overflowY: 'auto' }}>
            {messages.length === 0 ? (
              <p>{t('noNewMessages')}</p>
            ) : (
              <ListGroup variant="flush">
                {messages.map((msg, index) => {
                  const isOwnMessage = msg.senderId === registeredPlayerId;
                  return (
                    <ListGroup.Item
                      key={msg.id || index}
                      className={`d-flex ${isOwnMessage ? 'justify-content-end' : 'justify-content-start'} align-items-start mb-2`}
                      style={{ backgroundColor: isOwnMessage ? '#dcf8c6' : '#e0e0e0', borderRadius: '10px', maxWidth: '75%' }}
                    >
                      <div className={`d-flex flex-column ${isOwnMessage ? 'align-items-end' : 'align-items-start'}`}>
                        <div className="fw-bold">{msg.senderName || msg.senderId}</div>
                        {msg.content}
                      </div>
                      <Badge bg="secondary" pill className="ms-2">
                        {
                          typeof msg.timestamp === 'string'
                            ? new Date(parseInt(msg.timestamp, 10)).toLocaleTimeString()
                            : new Date(msg.timestamp).toLocaleTimeString()
                        }
                      </Badge>
                    </ListGroup.Item>
                  );
                })}
                <div ref={messagesEndRef} /> {/* Element to scroll to */}
              </ListGroup>
            )}

            {messages.length > 0 && (
              <div className="mt-3">
                <InputGroup>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    placeholder={t('typeYourReply')}
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                  />
                  <Button variant="primary" onClick={handleReply}>
                    {t('reply')}
                  </Button>
                </InputGroup>
              </div>
            )}

            {gameLocation && (
              <div className="mt-3">
                <h5>{t('gameLocation')}</h5>
                <MapContainer center={mapCenter} zoom={13} style={{ height: '150px', width: '100%' }}>
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <Marker position={[gameLocation.latitude, gameLocation.longitude]}>
                    <Popup>{messages[messages.length - 1]?.gameDate} {messages[messages.length - 1]?.gameTime} {messages[messages.length - 1]?.gameAddress || t('gameLocation')}</Popup>
                  </Marker>
                </MapContainer>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default PlayerChat;
